import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CategoryManager = ({ categories, onCategoryChange, onClose }) => {
  const { t } = useLanguage();
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParent, setNewCategoryParent] = useState('');
  const [loading, setLoading] = useState(false);

  const rootCategories = categories.filter(c => !c.parent_id);
  
  const getChildren = (parentId) => {
    return categories.filter(c => c.parent_id === parentId);
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error(t('category_name'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newCategoryName,
          parent_id: newCategoryParent || null
        })
      });

      if (response.ok) {
        toast.success(t('category_created'));
        setNewCategoryName('');
        setNewCategoryParent('');
        onCategoryChange();
      } else {
        throw new Error('Failed to create category');
      }
    } catch (error) {
      console.error('Failed to create category:', error);
      toast.error(t('error_occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async (categoryId, name) => {
    if (!name.trim()) return;

    try {
      const response = await fetch(`${API_URL}/api/categories/${categoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name })
      });

      if (response.ok) {
        toast.success(t('save') + '!');
        setEditingCategory(null);
        onCategoryChange();
      } else {
        throw new Error('Failed to update category');
      }
    } catch (error) {
      console.error('Failed to update category:', error);
      toast.error(t('error_occurred'));
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm(t('delete_category_confirm') || 'Delete this category and all subcategories?')) return;

    try {
      const response = await fetch(`${API_URL}/api/categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(t('category_deleted'));
        onCategoryChange();
      } else {
        throw new Error('Failed to delete category');
      }
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error(t('error_occurred'));
    }
  };

  const handleMoveCategory = async (categoryId, newParentId) => {
    try {
      const response = await fetch(`${API_URL}/api/categories/${categoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ parent_id: newParentId || null })
      });

      if (response.ok) {
        toast.success(t('save') + '!');
        onCategoryChange();
      } else {
        throw new Error('Failed to move category');
      }
    } catch (error) {
      console.error('Failed to move category:', error);
      toast.error(t('error_occurred'));
    }
  };

  const CategoryItem = ({ category, level = 0 }) => {
    const children = getChildren(category.category_id);
    const isEditing = editingCategory === category.category_id;
    const [editName, setEditName] = useState(category.name);

    return (
      <div className="mb-1">
        <div
          className={`flex items-center gap-2 p-2 rounded-lg hover:bg-accent/10 transition-colors`}
          style={{ marginLeft: level * 20 }}
        >
          <svg className="w-4 h-4 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>

          {isEditing ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={() => handleUpdateCategory(category.category_id, editName)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleUpdateCategory(category.category_id, editName);
                if (e.key === 'Escape') setEditingCategory(null);
              }}
              className="flex-1 px-2 py-1 text-sm border border-input rounded bg-background focus:outline-none focus:ring-1 focus:ring-ring"
              autoFocus
            />
          ) : (
            <span className="flex-1 text-sm text-foreground">{category.name}</span>
          )}

          <div className="flex items-center gap-1">
            <button
              onClick={() => setEditingCategory(category.category_id)}
              className="p-1 hover:bg-accent/20 rounded transition-colors"
              title={t('edit')}
            >
              <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => handleDeleteCategory(category.category_id)}
              className="p-1 hover:bg-destructive/10 rounded transition-colors"
              title={t('delete')}
            >
              <svg className="w-4 h-4 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
        
        {children.map(child => (
          <CategoryItem key={child.category_id} category={child} level={level + 1} />
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      
      <div className="relative bg-card rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">{t('categories')}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent/10 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Create New Category */}
          <div className="mb-6 p-4 bg-secondary/30 rounded-lg">
            <h3 className="text-sm font-medium text-foreground mb-3">{t('new_category')}</h3>
            <div className="space-y-3">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder={t('category_name')}
                className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background"
                data-testid="new-category-name"
              />
              <select
                value={newCategoryParent}
                onChange={(e) => setNewCategoryParent(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background"
                data-testid="new-category-parent"
              >
                <option value="">{t('no_category')} (raiz)</option>
                {categories.map(cat => (
                  <option key={cat.category_id} value={cat.category_id}>
                    {'─'.repeat(cat.path.length - 1)} {cat.name}
                  </option>
                ))}
              </select>
              <button
                onClick={handleCreateCategory}
                disabled={loading || !newCategoryName.trim()}
                className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50"
                data-testid="create-category-button"
              >
                {loading ? t('loading') : t('new_category')}
              </button>
            </div>
          </div>

          {/* Categories List */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">{t('categories')}</h3>
            {categories.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('no_categories')}
              </p>
            ) : (
              <div className="space-y-1">
                {rootCategories.map(category => (
                  <CategoryItem key={category.category_id} category={category} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryManager;
